<template>
    <div class="manage-users-modal">
        <hox-modal fullscreen :max-width-px="1400" @close="close">
            <template #header>Workspace Settings</template>

            <template #tabs>
                <NavigationTabs color="tertiary">
                    <NavigationTab
                        v-if="userCanManageUsers"
                        data-testid="workspace-settings__tabs-people"
                        :is-active="isTabActive(Tabs.People)"
                        @click="setActive(Tabs.People)"
                    >
                        People
                        <span>({{ users.length }})</span>
                    </NavigationTab>
                    <NavigationTab
                        v-if="userCanReceiveAllNotifications"
                        data-testid="workspace-settings__tabs-notifications"
                        :is-active="isTabActive(Tabs.Notifications)"
                        @click="setActive(Tabs.Notifications)"
                    >
                        Notifications
                    </NavigationTab>
                </NavigationTabs>
            </template>

            <div class="manage-users-modal__tab-content">
                <div v-if="showNotificationSettings" class="manage-users-modal__notification-tab">
                    <Spin v-if="loadingNotificationSettings" />
                    <div v-else class="notification-settings">
                        <div class="notification-settings-group">
                            <h3>Annotations</h3>
                            <LabeledSwitch v-model="annotationSettings.mentions" type="info">
                                <div class="notification-label">
                                    You will always be notified when someone @mentions you, or comments on a discussion
                                    that you are involved with.
                                </div>
                            </LabeledSwitch>
                            <LabeledSwitch v-model="annotationSettings.all" type="info">
                                <div class="notification-label">
                                    You will always be notified when external reviewers annotate any of the banners in
                                    your campaign
                                </div>
                            </LabeledSwitch>
                        </div>

                        <div class="notification-settings-group">
                            <h3>Status Changes and Approvals</h3>
                            <div class="notification-label notification-label--shared">
                                You will always be notified when any of the banners in your campaigns are set to:
                            </div>
                            <LabeledSwitch v-model="statusChangesSettings.approved" type="info">
                                <div class="notification-label"><strong>Status: approved</strong></div>
                            </LabeledSwitch>
                            <LabeledSwitch v-model="statusChangesSettings.inReview" type="info">
                                <div class="notification-label"><strong>Status: in review</strong></div>
                            </LabeledSwitch>

                            <LabeledSwitch v-model="statusChangesSettings.rejected" type="info">
                                <div class="notification-label"><strong>Status: rejected</strong></div>
                            </LabeledSwitch>
                        </div>

                        <div class="notification-settings-group">
                            <h3>New users</h3>
                            <LabeledSwitch v-model="newUserJoins" type="info">
                                <div class="notification-label">
                                    You will always be notified when a new user joins one of your brands and campaigns
                                </div>
                            </LabeledSwitch>
                        </div>

                        <div class="notification-settings-group">
                            <h3>Email frequency</h3>

                            <RadioGroup v-model="notificationFrequency" class="notification-radio-group" vertical>
                                <Radio class="notification-radio" :label="NotificationFrequencies.Everytime">
                                    <span class="notification-label">Send me an email for any of the cases above</span>
                                </Radio>
                                <Radio class="notification-radio" :label="NotificationFrequencies.Daily">
                                    <span class="notification-label">
                                        Send me an email every 24 hours, including all updates
                                    </span>
                                </Radio>
                            </RadioGroup>

                            <Button
                                v-if="notificationFrequency === NotificationFrequencies.Daily"
                                type="text"
                                class="notification-email-digest"
                                @click="sendEmailDigest"
                            >
                                Send me the email digest
                            </Button>
                        </div>
                    </div>
                </div>
                <template v-if="isTabActive(Tabs.People)">
                    <div class="manage-users-modal__add-client-wrapper">
                        <Button
                            class="pull-right"
                            type="text"
                            icon="md-person-add"
                            data-testid="add-user__button"
                            @click="onAddUser"
                        >
                            Add user
                        </Button>
                    </div>

                    <div class="manage-users-modal-content">
                        <Spin v-if="$apollo.queries.users.loading" fixed></Spin>

                        <UserRolesList
                            v-else
                            :users="users"
                            @roleChanged="onUserRoleChanged"
                            @userDeleted="refreshUserData"
                        />
                    </div>

                    <UserAccessScopeModal
                        v-if="showNewUserModal"
                        new-user
                        @userCreated="refreshUserData"
                        @close="showNewUserModal = false"
                    />
                </template>
            </div>

            <template #footer>
                <Button type="text" data-testid="workspace-settings-modal__cancel_button" @click="close">Cancel</Button>
                <Button
                    type="primary"
                    :loading="isSaving"
                    data-testid="workspace-settings-modal__save_button"
                    @click="save"
                >
                    Save All
                </Button>
            </template>
        </hox-modal>
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import { USER_ACTIONS } from "shared-utils/enums/user";

import bus from "@/bus";

import UsersQuery from "@/apollo/queries/UsersWithScopeEntities.gql";
import UsersNotificationSettingsQuery from "@/apollo/queries/UsersNotificationSettings.gql";
import SendMeEmailDigestQuery from "@/apollo/queries/v2/SendMeEmailDigest.gql";

import UpdateUserMutation from "@/apollo/mutations/UpdateUser.gql";
import SetUserNotificationSettings from "@/apollo/mutations/SetUserNotificationSettings.gql";

import HoxModal from "@/components/Modal/Modal/Modal.vue";
import NavigationTab from "@/components/common/NavigationTabs/Tab.vue";
import NavigationTabs from "@/components/common/NavigationTabs/Container.vue";
import UserRolesList from "@/components/ManageUsers/UserRolesList.vue";
import UserAccessScopeModal from "@/components/ManageUsers/UserAccessScopeModal.vue";
import LabeledSwitch from "@/components/Campaign/LabeledSwitch.vue";
import type { User, UserNotificationSettings } from "@/types/types";
import { userCan } from "@/services/Auth";

const Tabs = {
    People: "people",
    Notifications: "notification"
};

const NotificationFrequencies = {
    Everytime: "everytime",
    Daily: "daily"
};

export default defineComponent({
    name: "ManageUserModal",
    components: {
        LabeledSwitch,
        UserAccessScopeModal,
        HoxModal,
        NavigationTab,
        NavigationTabs,
        UserRolesList
    },

    emits: ["close"],

    data() {
        return {
            activeTab: Tabs.Notifications,
            showNewUserModal: false,
            users: [] as User[],
            updatedRoles: {} as Record<string, { userId: string; roleId: string }>,
            annotationSettings: {
                mentions: true,
                all: true
            },
            statusChangesSettings: {
                approved: true,
                inReview: true,
                rejected: true
            },
            newUserJoins: true,
            notificationFrequency: NotificationFrequencies.Everytime,
            isSaving: false,
            Tabs,
            NotificationFrequencies,
            loadingNotificationSettings: false
        };
    },

    computed: {
        userCanManageUsers() {
            return userCan(USER_ACTIONS.canManageUsers);
        },

        // Every notification option is gated by this action on the backend: reading,
        // saving and the emails themselves. Without it the whole tab would just error on save.
        userCanReceiveAllNotifications() {
            return userCan(USER_ACTIONS.canReceiveAllNotifications);
        },

        showNotificationSettings() {
            return this.userCanReceiveAllNotifications && this.isTabActive(Tabs.Notifications);
        }
    },

    watch: {
        userCanManageUsers() {
            if (!this.userCanManageUsers) {
                this.$snackbar.warning("You are not authorized to manage users");
                this.activeTab = Tabs.Notifications;
            }
        }
    },

    created() {
        if (this.userCanManageUsers) {
            this.activeTab = Tabs.People;
        }
    },

    methods: {
        close() {
            this.$emit("close");
        },

        isTabActive(tab: (typeof Tabs)[keyof typeof Tabs]) {
            return this.activeTab === tab;
        },

        onAddUser() {
            this.showNewUserModal = true;
        },

        onUserRoleChanged(newRoleData: { userId: string; roleId: string }) {
            this.updatedRoles[newRoleData.userId] = newRoleData;
        },

        refreshUserData() {
            this.$apollo.queries.users.refetch();
        },

        async save() {
            this.isSaving = true;

            if (this.showNotificationSettings) {
                const notificationSettings = {
                    annotationSettings: this.annotationSettings,
                    statusChangesSettings: this.statusChangesSettings,
                    newUserJoins: this.newUserJoins,
                    notificationFrequency: {
                        everytime: this.notificationFrequency === NotificationFrequencies.Everytime,
                        daily: this.notificationFrequency === NotificationFrequencies.Daily
                    }
                };

                try {
                    await this.$apollo.mutate({
                        mutation: SetUserNotificationSettings,
                        variables: {
                            notificationSettings
                        },
                        refetchQueries: [
                            {
                                query: UsersNotificationSettingsQuery
                            }
                        ]
                    });
                    this.$snackbar.success("Notification settings updated successfully");
                } catch (e) {
                    this.$snackbar.error("Failed to updated the notification settings", (e as Error).message);
                }
            }

            const userIds = Object.keys(this.updatedRoles);

            if (!userIds.length) {
                this.isSaving = false;
                this.close();

                return;
            }

            try {
                await Promise.all(
                    userIds.map(userId =>
                        this.$apollo.mutate({
                            mutation: UpdateUserMutation,
                            variables: {
                                id: this.updatedRoles[userId].userId,
                                roleId: this.updatedRoles[userId].roleId
                            },
                            refetchQueries: [{ query: UsersQuery }]
                        })
                    )
                );

                this.$snackbar.success(`${userIds.length} user roles updated successfully`);
            } catch (e) {
                this.$snackbar.error("Failed to updated user roles", (e as Error).message);
            }

            this.isSaving = false;
            this.close();
        },

        setActive(tab: (typeof Tabs)[keyof typeof Tabs]) {
            this.activeTab = tab;
        },

        async sendEmailDigest() {
            try {
                await this.$apollo.query({
                    fetchPolicy: "no-cache",
                    query: SendMeEmailDigestQuery
                });

                this.$snackbar.success("Email digest was sent successfully to your email");
            } catch (e) {
                this.$snackbar.error("Failed to send the email digest", (e as Error).message);
            }
        }
    },

    apollo: {
        users: {
            query: UsersQuery,
            fetchPolicy: "network-only",
            skip() {
                return !this.userCanManageUsers;
            },
            error(e) {
                bus.$emit("apolloErrorEvent", e as Error);
            }
        },
        userNotificationSettings: {
            query: UsersNotificationSettingsQuery,
            result({ data }: { data: { userNotificationSettings: UserNotificationSettings } }) {
                if (data && data.userNotificationSettings) {
                    this.annotationSettings = {
                        mentions: data.userNotificationSettings.annotationSettings.mentions,
                        all: data.userNotificationSettings.annotationSettings.all
                    };
                    this.statusChangesSettings = {
                        approved: data.userNotificationSettings.statusChangesSettings.approved,
                        inReview: data.userNotificationSettings.statusChangesSettings.inReview,
                        rejected: data.userNotificationSettings.statusChangesSettings.rejected
                    };
                    this.newUserJoins = data.userNotificationSettings.newUserJoins;
                    this.notificationFrequency = data.userNotificationSettings.notificationFrequency.everytime
                        ? NotificationFrequencies.Everytime
                        : NotificationFrequencies.Daily;
                }
            },
            skip() {
                return !this.showNotificationSettings;
            },
            loadingKey: "loadingNotificationSettings",
            error(e) {
                bus.$emit("apolloErrorEvent", e as Error);
            }
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.manage-users-modal {
    &-alert {
        .alert__icon-container {
            padding-bottom: 0;
        }
    }

    &__tab-content {
        width: 100%;
    }

    &__notification-tab {
        display: flex;
    }

    &__add-client-wrapper {
        float: right;
        width: 100%;
    }
}

.notification-label {
    width: 650px;
    color: $darktheme20;
    padding-right: $spacing-large;

    &--shared {
        padding-top: $spacing-smaller;
    }
}

.labeled-switch {
    padding: $spacing-small 0;
}

.notification-settings-group {
    margin-top: $spacing-large;
    padding-bottom: $spacing-large;
    border-bottom: 1px solid $grey3;
}

.notification-radio-group {
    padding-top: $spacing-small;
}

.notification-settings {
    padding-top: $spacing-large;
    margin: 0 $spacing-large;
    width: 750px;
}

.notification-email-digest {
    margin-top: 20px;
}
</style>
